import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionTemplate, useMotionValue, useSpring } from "framer-motion";
import aPropos from "../images/aPropos.png";
import logoLT from "../images/logo-latuque.png";
import { PageMeta } from "../seo/PageMeta";

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { label: "Accueil", href: "/" },
    { label: "Nos projets", href: "/projet" },
    { label: "Nous contacter", href: "/contact" },
    { label: "À propos", href: "/about" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-0 transition-all duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-6xl py-4 md:py-5">
        <div className="flex items-center justify-between md:hidden">
          <a href="/" className="text-sm font-semibold tracking-wide text-[#1a1a2e]">
            LMS
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Ouvrir le menu"
            className="rounded-lg p-2 text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
          >
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current mb-1.5" />
            <span className="block h-0.5 w-6 bg-current" />
          </button>
        </div>

        <div className="hidden md:flex justify-center gap-10">
          {navLinks.map(({ label, href }) => (
            <motion.a
              key={label}
              href={href}
              className="relative text-sm font-medium text-[#1a1a2e] tracking-wide"
              whileHover="hover"
              animate="rest"
            >
              {label}
              <motion.span
                className="absolute -bottom-0.5 left-0 h-[1.5px] bg-[#1a1a2e] block"
                variants={{
                  rest: { width: "0%" },
                  hover: { width: "100%" },
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </motion.a>
          ))}
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="mt-3 rounded-2xl border border-[#1a1a2e]/10 bg-white/95 p-3 shadow-md md:hidden"
            >
              <div className="flex flex-col">
                {navLinks.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
                  >
                    {label}
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

function FontLoader() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";
    document.head.appendChild(link);
  }, []);
  return null;
}

const aboutSections = [
  {
    title: "Qu'est-ce que LMS Technologies",
    body: (
      <>
        LMS Technologies S.E.N.C. est une société de développement de
        <strong> solutions numériques sur mesure</strong> basée à La Tuque, Québec.
        Nous développons des <strong>sites web</strong>, des <strong>outils de gestion</strong> et notre
        propre <strong>logiciel CRM</strong> pour aider les PME à mieux gérer leur clientèle
        et leur croissance.
      </>
    ),
  },
  {
    title: "Notre histoire",
    body: (
      <>
        Fondée par deux natifs de La Tuque, LMS Technologies est le fruit de la
        collaboration entre <strong>Samuel Girard</strong> et <strong>Louis-Mathieu Dubé</strong>. Une combinaison <strong>tech + business</strong> qui permet d'offrir des solutions utiles,
        concrètes et adaptées à chaque entreprise. <strong>Samuel</strong>, technicien en informatique et fait en ce moment son baccalauréat
        en informatique à l'UQTR, cumule près de <strong>3 ans d'expérience</strong> en développement web. <strong>Louis-Mathieu</strong>, quant à lui, poursuit
        un baccalauréat en finance à l'Université Laval, une expertise qui ancre
        chaque projet dans une réalité d'affaires concrète.
      </>
    ),
  },
  {
    title: "Nos rôles et responsabilités",
    body: (
      <>
        <strong>Louis-Mathieu Dubé - Cofondateur, développement des affaires</strong>
        <br />
        Passionné par les relations clients et la croissance des entreprises, il
        accompagne chaque organisation dans l'identification de ses besoins numériques.
        <br />
        <br />
        <strong>Samuel Girard - Cofondateur, développement technologique</strong>
        <br />
        Architecte des solutions numériques de LMS Technologies, il conçoit et
        développe les outils qui propulsent les entreprises vers l'avant.
      </>
    ),
  },
];

function AboutContent() {
  const accentStyles = [
    {
      pill: "bg-[#0C1925] text-white",
      border: "border-[#0C1925]/15",
      glow: "hover:shadow-[0px_18px_42px_rgba(12,25,37,0.18)]",
    },
    {
      pill: "bg-[#4F9ECF] text-white",
      border: "border-[#4F9ECF]/25",
      glow: "hover:shadow-[0px_18px_42px_rgba(79,158,207,0.22)]",
    },
    {
      pill: "bg-[#1a1a2e] text-white",
      border: "border-[#1a1a2e]/20",
      glow: "hover:shadow-[0px_18px_42px_rgba(26,26,46,0.2)]",
    },
  ];

  return (
    <section
      className="min-h-screen pt-36 pb-20 px-6"
      style={{ background: "linear-gradient(180deg, #F8FBFF 0%, #DCEEFF 100%)" }}
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-14"
        >
        <img className="mb-4 mx-auto block" src={aPropos} alt="À propos" height={400} width={400} />
        <img className="mb-2 mx-auto block" src={logoLT} alt="À propos" height={70} width={70} />


        </motion.div>

        <div className="grid gap-8">
          {aboutSections.map((section, index) => (
            <AboutCard
              key={section.title}
              section={section}
              index={index}
              accent={accentStyles[index % accentStyles.length]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutCard({ section, index, accent }) {
  const cardBorderRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const rawX = useMotionValue(-1);
  const rawY = useMotionValue(-1);
  const mouseX = useSpring(rawX, { stiffness: 110, damping: 22, mass: 0.4 });
  const mouseY = useSpring(rawY, { stiffness: 110, damping: 22, mass: 0.4 });

  const handleMouseMove = (e) => {
    if (!cardBorderRef.current) return;
    const rect = cardBorderRef.current.getBoundingClientRect();
    rawX.set(e.clientX - rect.left);
    rawY.set(e.clientY - rect.top);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (!cardBorderRef.current) return;
    const rect = cardBorderRef.current.getBoundingClientRect();
    rawX.set(rect.width / 2);
    rawY.set(rect.height + 80);
  };

  const animatedBorder = useMotionTemplate`radial-gradient(
    320px circle at ${mouseX}px ${mouseY}px,
    rgba(79, 158, 207, 1),
    rgba(79, 158, 207, 0.58) 38%,
    rgba(79, 158, 207, 0.22) 64%,
    rgba(79, 158, 207, 0) 100%
  )`;

  return (
    <motion.div
      ref={cardBorderRef}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.08 * index }}
      whileHover={{ y: -7, transition: { duration: 0.12, ease: "easeOut" } }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={`rounded-3xl p-[2px] ${accent.glow} ${
        index % 2 === 0 ? "md:ml-0 md:mr-16" : "md:mr-0 md:ml-16"
      }`}
      style={{
        background: isHovered ? animatedBorder : "rgba(79, 158, 207, 0.45)",
        boxShadow: isHovered
          ? "0px 12px 38px rgba(79, 158, 207, 0.3)"
          : "0px 10px 26px rgba(12, 25, 37, 0.12)",
      }}
    >
      <article className={`group rounded-[22px] border bg-white p-6 md:p-8 ${accent.border}`}>
        <div className="flex items-start justify-between gap-4">
          <span
            className={`inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase ${accent.pill}`}
          >
            {index + 1}
          </span>
        </div>

        <h2 className="mt-5 text-2xl md:text-3xl font-semibold text-[#1a1a2e] tracking-tight">
          {section.title}
        </h2>

        <div className="mt-5 min-h-[100px] rounded-2xl bg-white text-[#1a1a2e]/55 leading-relaxed [&_strong]:font-semibold [&_strong]:text-[#1a1a2e]">
          {section.body || "Ton texte ici."}
        </div>
      </article>
    </motion.div>
  );
}

function Footer() {
  return (
    <footer
      className="bg-[#0C1925] border-t border-white/10 px-6 py-6 md:px-10"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-4 md:flex-row">
        <p className="text-center text-sm text-white/40 md:text-left">
          © {new Date().getFullYear()} LMS Technologies. Tous droits réservés.
        </p>

        <div className="flex w-full items-center justify-center gap-5 text-center text-sm lg:w-auto lg:justify-start lg:text-left">
          <a href="/condition" className="text-white/50 transition-colors hover:text-white">
            Conditions d&apos;utilisation
          </a>
          <span className="text-white/20">|</span>
          <a href="/confidentiality" className="text-white/50 transition-colors hover:text-white">
            Politique de confidentialite
          </a>
        </div>

        {/* Réseaux sociaux */}
        <div className="flex items-center gap-6">
        {[
          {
            label: "Instagram",
            href: "https://instagram.com",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
              </svg>
            ),
          },
          {
            label: "Facebook",
            href: "https://facebook.com",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            ),
          },
        ].map(({ label, href, icon }) => (
          <motion.a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="text-white/40 hover:text-white transition-colors"
            whileHover={{ scale: 1.2, y: -2 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            {icon}
          </motion.a>
        ))}
        </div>
      </div>
    </footer>
  );
}

export default function About() {
  return (
    <>
      <PageMeta
        title="À propos"
        description="LMS Technologies S.E.N.C. — solutions numériques sur mesure pour les PME : sites web, outils de gestion et logiciel CRM. Basée à La Tuque, Québec."
        path="/about"
      />
      <FontLoader />
      <Navbar />
      <main style={{ fontFamily: "'Inter', sans-serif" }}>
        <AboutContent />
      </main>
      <Footer/>
    </>
  );
}